import React, { useState, useEffect, useCallback } from 'react';
import './HomePage.css';
import { PieChart, Pie, Cell, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';

import {
  getFilteredNavItemsWithIcons,
  handleNavClick as routesHandleNavClick,
  hasPermission,
  getPageNameByPath
} from '../../config/routes';
import { useLocation } from 'react-router-dom';
import { todayYmd, addDaysYmd } from '../../utils/timezone';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28BFE', '#FF6B9D', '#43D9AD'];

const today = todayYmd();
const thirtyDaysAgo = addDaysYmd(-30);


function ChangeLabel({ value }) {
  if (value === null || value === undefined) return <span className="change-neutral">— no prev data</span>;
  const pct = (Math.abs(value) * 100).toFixed(1);
  return value >= 0
    ? <span className="change-up">↑{pct}% vs previous period</span>
    : <span className="change-down">↓{pct}% vs previous period</span>;
}

function HomePage({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const location = useLocation();

  const currentPageName = getPageNameByPath(location.pathname) || 'Home';

  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [analyticsMode, setAnalyticsMode] = useState('sales');
  const [analyticsMonths, setAnalyticsMonths] = useState(12);
  const [analyticsData, setAnalyticsData] = useState([]);

  // 定义 fetchAnalytics
  const fetchAnalytics = useCallback(async (tenantRef, months) => {
    if (!tenantRef) return;
    try {
      const res = await fetch(
        buildApiUrl(`/sales/analytics?tenantReference=${tenantRef}&months=${months}`),
        { headers: getAuthHeaders() }
      );
      const json = await res.json();
      if (json.code === 200 && json.data) setAnalyticsData(json.data);
      else setAnalyticsData([]);
    } catch (err) {
      console.error('Failed to fetch analytics', err);
    }
  }, []);

  // 定义 fetchDashboard
  const fetchDashboard = useCallback(async (tenantRef, start, end) => {
    if (!tenantRef) return;
    setLoading(true);
    try {
      const res = await fetch(
        buildApiUrl(`/sales/dashboard?tenantReference=${tenantRef}&startDate=${start}&endDate=${end}`),
        { headers: getAuthHeaders() }
      );
      const json = await res.json();
      if (json.code === 200 && json.data) {
        setData(json.data);
      } else {
        setData(null);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 使用 useEffect 调用
  useEffect(() => {
    if (selectedRestaurant?.reference) {
      fetchDashboard(selectedRestaurant.reference, startDate, endDate);
      fetchAnalytics(selectedRestaurant.reference, analyticsMonths);
    }
  }, [selectedRestaurant, startDate, endDate, analyticsMonths, fetchDashboard, fetchAnalytics]);

  useEffect(() => {
    if (selectedRestaurant?.reference) {
      fetchAnalytics(selectedRestaurant.reference, analyticsMonths);
    }
  }, [analyticsMonths, selectedRestaurant, fetchAnalytics]);

  const handleApply = () => {
    setShowDatePicker(false);
    if (selectedRestaurant?.reference) {
      fetchDashboard(selectedRestaurant.reference, startDate, endDate);
    }
  };

  const handleNavClick = (item) => {
    if (!hasPermission(item)) {
      console.warn(`No permission to access: ${item}`);
      return;
    }
    routesHandleNavClick(item, onNavigate);
  };


  

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (


 
    <div className="homepage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li
              key={item}
              className={currentPageName === item ? 'active' : ''}
              onClick={() => handleNavClick(item)}
            >
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="dashboard">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <HasPermi permission={['home:Dashboard']}>
            <div className="date-picker-container">

              <button className="date-picker-btn" onClick={() => setShowDatePicker(!showDatePicker)}>
                📅 Date Range: {startDate} – {endDate}
              </button>

              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <div className="date-input-group">
                    <label>Start Date:</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                  </div>
                  <div className="date-input-group">
                    <label>End Date:</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                  </div>
                  <button className="apply-date-btn" onClick={handleApply}>Apply</button>
                </div>
              )}
            </div>
          </HasPermi>
        </div>


        <HasPermi permission={['home:Dashboard']}>
          {loading && <div className="dashboard-loading">Loading…</div>}

          {!loading && !data && (
            <div className="dashboard-empty">No data for selected period.</div>
          )}

          {!loading && data && (
            <>
              <div className="dashboard-cards">
                <div className="dashboard-card">
                  <div className="card-title">Total Sales</div>
                  <div className="card-value">${Number(data.totalRevenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="card-desc"><span className="date-range">{startDate} – {endDate}</span> <ChangeLabel value={data.revenueChange} /></div>
                </div>
                <div className="dashboard-card">
                  <div className="card-title">Total Transactions</div>
                  <div className="card-value">{data.totalOrders.toLocaleString()}</div>
                  <div className="card-desc"><span className="date-range">{startDate} – {endDate}</span> <ChangeLabel value={data.ordersChange} /></div>
                </div>
              </div>

              <div className="dashboard-top-row">
                <div className="dashboard-bestsellers">
                  <h2>Bestsellers Top 4</h2>
                  {data.bestsellers.length === 0 ? (
                    <div className="dashboard-empty">No sales data.</div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Dish</th>
                          <th>Sold</th>
                          <th>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                      {data.bestsellers.map((item, i) => (
                        <tr key={i}>
                          <td>
                            <div className="dish-cell">
                              {item.itemPic
                                ? <img
                                    src={item.itemPic}
                                    alt={item.itemName}
                                    className="dish-thumb"
                                  />
                                : <div className="dish-thumb dish-thumb-placeholder" />}
                              <span className="dish-name">{item.itemName}</span>
                            </div>
                          </td>
                          <td>{item.totalQty}</td>
                          <td>${Number(item.totalAmount).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  )}
                </div>

                <div className="chart-block">
                  <h2>Sales Across Categories (Pie Chart)</h2>
                  {data.categorySales.length === 0 ? (
                    <div className="dashboard-empty">No category data.</div>
                  ) : (
                    <div className="chart-container">
                      <ResponsiveContainer width="60%" height={250}>
                        <PieChart>
                          <Pie
                            data={data.categorySales}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ value }) => `$${Number(value).toFixed(2)}`}
                            labelLine={true}
                          >
                            {data.categorySales.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="chart-legend">
                        <h3>Category Legend:</h3>
                        {data.categorySales.map((entry, index) => (
                          <div key={entry.name} className="legend-item">
                            <span className="legend-color" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                            <span className="legend-text">{entry.name}: ${Number(entry.value).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </HasPermi>

        <HasPermi permission={['home:Analysis']}>
          <div className="analytics-block">
            <div className="analytics-header">
              <h2>Analytics</h2>
              <div className="analytics-controls">
                <div className="analytics-toggle">
                  <button
                    className={`toggle-btn ${analyticsMode === 'sales' ? 'active' : ''}`}
                    onClick={() => setAnalyticsMode('sales')}
                  >Sales</button>
                  <button
                    className={`toggle-btn ${analyticsMode === 'reservation' ? 'active' : ''}`}
                    onClick={() => setAnalyticsMode('reservation')}
                  >Reservation</button>
                </div>
                <select
                  className="analytics-months-select"
                  value={analyticsMonths}
                  onChange={e => setAnalyticsMonths(Number(e.target.value))}
                >
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                  <option value={18}>18 months</option>
                  <option value={24}>24 months</option>
                </select>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={analyticsData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  domain={[0, 'auto']}
                  tickFormatter={(v) => analyticsMode === 'sales'
                    ? (v >= 1000 ? `$${(v / 1000).toFixed(1)}K` : `$${v}`)
                    : v}
                />
                <Tooltip
                  formatter={(v) => analyticsMode === 'sales'
                    ? [`$${Number(v).toFixed(2)}`, 'Sales ($)']
                    : [v, 'Reservations (count)']}
                />
                <Legend />
                {analyticsMode === 'sales'
                  ? <Line type="monotone" dataKey="sales" name="Sales ($)" stroke="#0088FE" strokeWidth={2} dot={{ r: 3 }} />
                  : <Line type="monotone" dataKey="reservations" name="Reservations (count)" stroke="#00C49F" strokeWidth={2} dot={{ r: 3 }} />
                }
              </LineChart>
            </ResponsiveContainer>
          </div>
        </HasPermi>
      </main>
    </div>

    
  );
}

export default HomePage;